import { useCallback, useContext, useMemo, useRef } from "react";
import { QueryClientContext } from "@tanstack/react-query";
import { useTracking } from "@core/features/tracking";
import { quizEventBus, type QuizEventContext } from "@core/features/quiz";
import { getFormSource, rememberCta } from "./utils/attribution";
import { intakeFormQuery } from "./forms/IntakeForm/intakeFormQuery";

/**
 * The marketing site's analytics events.
 *
 * These names are the measurement plan agreed for the site, so they are
 * constants rather than string literals: a typo in one of them does not fail,
 * it just quietly produces a funnel with a hole in it that nobody notices
 * until someone asks why the numbers do not add up.
 */
export const MARKETING_EVENT = {
  SECTION_VIEW: "section_view",
  CTA_CLICK: "cta_click",
} as const;

/**
 * Helpers for the marketing site's measurement.
 *
 * The events describe the top of a funnel: how many people saw a section and
 * how many clicked its call to action. What happens once somebody reaches a
 * form is measured as the answering of a quiz, by `useIntakeQuizTracking`.
 */
export function useMarketingTracking() {
  const { trackEvent } = useTracking();
  // Read without insisting on it, so a component drawn on its own, as in a
  // story or a test, still tracks, only without the form's ids.
  const queryClient = useContext(QueryClientContext);

  const trackSectionView = useCallback(
    (sectionId: string) => {
      trackEvent({
        name: MARKETING_EVENT.SECTION_VIEW,
        params: { section_id: sectionId },
      });
    },
    [trackEvent]
  );

  /**
   * A call to action was clicked.
   *
   * A CTA that leads to an intake form should pass `{ form_slug }`, because
   * that is what lets the funnel report attribute the click to an audience
   * rather than counting every CTA on the site together.
   *
   * The click then carries the form's `intake_form_id` and `quiz_id` too, as
   * every event about a form does. A button knows only the form's slug, so
   * the form's configuration is looked up, which is usually already loaded
   * and otherwise is wanted by the page the click leads to anyway. A click
   * whose form cannot be looked up is still sent, without them.
   */
  const trackCtaClick = useCallback(
    (ctaName: string, params: Record<string, unknown> = {}) => {
      const formSlug = typeof params.form_slug === "string" ? params.form_slug : undefined;
      const send = (ids: Record<string, unknown> = {}) =>
        trackEvent({
          name: MARKETING_EVENT.CTA_CLICK,
          params: { cta_name: ctaName, ...params, ...ids },
        });
      // Remembered against the form it leads to, so that what happens on the
      // form can be credited to this button.
      if (formSlug) rememberCta(ctaName, formSlug);
      if (!formSlug || !queryClient) {
        send();
        return;
      }
      // Settings already loaded are taken as they are, and a failed lookup
      // is not retried, so the click is never held back for long.
      queryClient.fetchQuery({ ...intakeFormQuery(formSlug), staleTime: 5 * 60 * 1000, retry: false }).then(
        (config) =>
          send(config ? { intake_form_id: config.id, quiz_id: config.quiz_id } : {}),
        () => send()
      );
    },
    [trackEvent, queryClient]
  );

  return { trackSectionView, trackCtaClick };
}

/**
 * Measurement of somebody filling in an intake form.
 *
 * A form's questions are a quiz, so filling one in is reported as answering a
 * quiz, through the events every quiz shares: it was put on screen, begun,
 * taken a screen at a time, got past its middle, and finished or failed. Each
 * says which quiz and which form, so a report can follow one form through all
 * of them, or set a form beside any other quiz. Each also says where the
 * person came from: the call to action that led to the form, when this visit
 * clicked one, and the campaign the visit arrived with. Only finishing leaves
 * anything in the database, so these are the sole record of everything before
 * it.
 *
 * `quizId` is absent only where a form is drawn without its configuration, as
 * in a story.
 */
export function useIntakeQuizTracking({
  quizId,
  formId,
  formSlug,
  anchor,
}: {
  quizId?: number;
  formId?: number;
  formSlug: string;
  /** For a form set into a longer page: the anchor it sits at */
  anchor?: string;
}) {
  const passedHalfway = useRef(false);

  return useMemo(() => {
    const id = quizId ?? 0;
    // Read as each event is sent, not once: the campaign is remembered when
    // the app starts, which may be after this form first renders.
    const where = (): QuizEventContext => ({
      intake_form_id: formId,
      form_slug: formSlug,
      ...getFormSource(formSlug, anchor),
    });

    return {
      loaded: () =>
        quizEventBus.emitEvent("quiz:loaded", { quizId: id, context: where() }),
      started: () =>
        quizEventBus.emitEvent("quiz:started", { quizId: id, context: where() }),
      /**
       * Somebody reached one screen of a form that has several. `kind` says
       * what the screen asks for (a day, a time, contact details, questions),
       * which is what makes the numbers readable without knowing each form's
       * order by heart. Reaching a screen past the middle is also reported
       * once on its own, for a form that has a middle: one answered on a
       * single screen does not, and reporting it there made "halfway" another
       * word for "opened".
       */
      step: (step: { index: number; count: number; kind: string }) => {
        quizEventBus.emitEvent("quiz:step", {
          quizId: id,
          step: step.index,
          stepCount: step.count,
          stepKind: step.kind,
          context: where(),
        });
        if (!passedHalfway.current && step.count > 1 && step.index * 2 > step.count) {
          passedHalfway.current = true;
          quizEventBus.emitEvent("quiz:halfway", { quizId: id, context: where() });
        }
      },
      failed: (reason: string) =>
        quizEventBus.emitEvent("quiz:error", { quizId: id, reason, context: where() }),
      /**
       * Somebody was stopped on a screen without anything being sent: the
       * times to book could not be read (`availability_failed`), none were
       * free (`nothing_available`), or Continue was pressed with answers
       * missing or refused (`invalid_answers`, naming the fields). Without
       * these a broken calendar looked like visitors losing interest.
       */
      problem: (reason: string, detail: { stepKind?: string; fields?: string[] } = {}) =>
        quizEventBus.emitEvent("quiz:problem", {
          quizId: id,
          reason,
          ...detail,
          context: where(),
        }),
      completed: (responseId?: number | null) =>
        quizEventBus.emitEvent("quiz:completed", {
          quizId: id,
          responseId,
          context: where(),
        }),
    };
  }, [quizId, formId, formSlug, anchor]);
}
